import type { Item, Level, Opening, Room, Scene, Surface, Vec2, Wall } from '../hp/types';
import { textureAffine } from '../hp/surfaceTransform';
import { defaultFloorSurface } from '../hp/defaults';
import { newUniqueId } from '../hp/uniqueIds';
import { t as tr, type TranslationKey } from '../../i18n/I18n';

export type EditResult = { level: Level; error?: undefined } | { level?: undefined; error: string };
export type WallProperties = Partial<Pick<Wall, 'start' | 'end' | 'thickness' | 'height'>> & { length?: number };
export type OpeningProperties = Partial<Pick<Opening, 'width' | 'height' | 'sill' | 'offset' | 'kind' | 'design' | 'modelRef' | 'caption' | 'flipped' | 'materials'>>;
export type VertexSnapTarget = { kind: 'vertex'; wallId: string; end: 'start' | 'end'; point: Vec2 }
  | { kind: 'wall'; wallId: string; point: Vec2; t: number };
export type EndpointConnection = { end: 'start' | 'end'; target: VertexSnapTarget };
export const MIN_WALL = 0.2;
/** Thickness a drawn wall starts with when no locale or choice says otherwise — Unity's 0.2 m. */
export const DEFAULT_WALL_THICKNESS = 0.2;
const EPS = 0.001;
/** Unity's Wall.Epsilon: wall ends this close are one joint when walls are connected or pulled together. */
const WALL_JOINT = 0.065;
const sub = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x - b.x, z: a.z - b.z });
const plus = (a: Vec2, b: Vec2): Vec2 => ({ x: a.x + b.x, z: a.z + b.z });
const mul = (p: Vec2, t: number): Vec2 => ({ x: p.x * t, z: p.z * t });
const dot = (a: Vec2, b: Vec2) => a.x * b.x + a.z * b.z;
const cross = (a: Vec2, b: Vec2) => a.x * b.z - a.z * b.x;
const distance = (a: Vec2, b: Vec2) => Math.hypot(a.x - b.x, a.z - b.z);
export const samePoint = (a: Vec2, b: Vec2) => distance(a, b) < EPS;
const unit = (w: Wall) => mul(sub(w.end, w.start), 1 / Math.max(distance(w.start, w.end), EPS));
const key = (p: Vec2) => `${Math.round(p.x / EPS)},${Math.round(p.z / EPS)}`;
const finite = (p: Vec2) => Number.isFinite(p.x) && Number.isFinite(p.z);
const snapPrecision = (p: Vec2): Vec2 => ({ x: Math.round(p.x * 1e4) / 1e4, z: Math.round(p.z * 1e4) / 1e4 });
const fail = (error: string): EditResult => ({ error });

export function createEmptyScene(): Scene {
  return { culture: 'ru-RU', revision: 0, appVersion: 'web', roomType: '', styles: [], warnings: [],
    assets: { models: [], textures: [], customTextures: [], modelTextures: [] },
    levels: [{ index: 0, uniqueId: newUniqueId(), name: '', lowerLevel: false, height: 2.7, outline: [], rooms: [], walls: [], openings: [], items: [] }] };
}

export function polygonArea(poly: Vec2[]): number {
  return poly.reduce((sum, p, i) => sum + cross(p, poly[(i + 1) % poly.length]), 0) / 2;
}

export function contains(poly: Vec2[], p: Vec2): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const a = poly[i], b = poly[j];
    if ((a.z > p.z) !== (b.z > p.z) && p.x < (b.x - a.x) * (p.z - a.z) / (b.z - a.z) + a.x) inside = !inside;
  }
  return inside;
}

export function projectPoint(p: Vec2, a: Vec2, b: Vec2): { point: Vec2; t: number; distance: number } {
  const edge = sub(b, a);
  const t = Math.max(0, Math.min(1, dot(sub(p, a), edge) / (dot(edge, edge) || 1)));
  const point = plus(a, mul(edge, t));
  return { point, t, distance: distance(point, p) };
}

function intersection(a: Wall, b: Wall): { t: number; u: number; p: Vec2 } | null {
  const r = sub(a.end, a.start), s = sub(b.end, b.start), delta = sub(b.start, a.start);
  const det = cross(r, s);
  if (Math.abs(det) < 1e-8) return null;
  const t = cross(delta, s) / det, u = cross(delta, r) / det;
  if (t < -EPS || t > 1 + EPS || u < -EPS || u > 1 + EPS) return null;
  return { t, u, p: snapPrecision(plus(a.start, mul(r, t))) };
}

/**
 * The fields derived from the ends and `forward`. `dir` runs start→end whichever way the wall
 * faces — the plan, the solids and the markers all measure along it from `start`. The authored
 * direction, which `theta` records and whose right-hand side is the wall's front, runs the same
 * way while `forward` holds and the opposite way once the wall was turned to face a room on the
 * left of its stroke; `normalIn` is that direction's right-hand side.
 */
function refreshWall(w: Wall): void {
  w.length = distance(w.start, w.end);
  w.center = mul(plus(w.start, w.end), 0.5);
  w.dir = unit(w);
  // 0 − x rather than −x: a zero component negated is −0, and atan2 would read it as −180°.
  const authored = w.forward ? w.dir : { x: 0 - w.dir.x, z: 0 - w.dir.z };
  w.normalIn = { x: -authored.z, z: authored.x };
  w.theta = Math.atan2(authored.z, authored.x) * 180 / Math.PI;
}

/**
 * Turn a wall round, so the face that was its back is its front. Nothing visible moves: each finish
 * stays on the face it was on, and every opening keeps its swing by having its own `flipped`
 * reversed, as it does when welded onto a wall facing the other way. Only the wall's notion of its
 * front changes, and with it the authored direction `theta` records.
 */
function turnWall(w: Wall, level: Level): void {
  w.forward = !w.forward; refreshWall(w);
  // A back without a finish of its own shows the front's, so that face keeps the front's finish.
  if (w.frontSurface || w.backSurface) [w.frontSurface, w.backSurface] = [w.backSurface ?? w.frontSurface, w.frontSurface];
  for (const o of level.openings) if (o.wallId === w.id) o.flipped = !o.flipped;
}

/** A rule the plan breaks, with a key by which the same fault can be recognised in another version of the level. */
type Fault = { message: TranslationKey; key: string; segment?: [Vec2, Vec2] };

/** A short return at a T-junction is a consequence of resizing, not a new 20 cm drawing stroke. */
function junctionReturn(level: Level, wall: Wall): boolean {
  if(wall.length<EPS || !isContourWall(level,wall))return false;
  const branches=[wall.start,wall.end].map(p=>{
    const directions:Vec2[]=[];
    for(const other of level.walls) {
      const far=samePoint(other.start,p)?other.end:samePoint(other.end,p)?other.start:null;
      if(!far||samePoint(far,p))continue;
      const d=mul(sub(far,p),1/distance(far,p));
      if(!directions.some(v=>dot(v,d)>1-EPS))directions.push(d);
    }
    return directions.length;
  });
  return branches.every(count=>count>=2)&&branches.some(count=>count>=3);
}

/** Every rule the level breaks, in the order the messages are reported. */
function levelFaults(level: Level): Fault[] {
  const faults: Fault[] = [];
  for (const w of level.walls) {
    if (!finite(w.start) || !finite(w.end) || !Number.isFinite(w.height) || !Number.isFinite(w.thickness)) faults.push({ message: '#GeometryEnterFiniteSizes', key: `wall:${w.id}:finite` });
    else if (distance(w.start, w.end) < MIN_WALL - EPS && !junctionReturn(level,w)) faults.push({ message: '#GeometryWallTooShort', key: `wall:${w.id}:short` });
    else if (w.thickness < 0.03 || w.thickness > 2 || w.height < 0.2 || w.height > 20) faults.push({ message: '#GeometryCheckWallSize', key: `wall:${w.id}:size` });
  }
  // Each room owns its contour, so two rooms may lie over one another and their walls may cross.
  // The rule still holds inside a single room, where crossing walls really are broken geometry.
  const rooms = new Set(level.rooms.map(r => r.index));
  const separateRooms = (a: Wall, b: Wall) => a.roomIndex !== b.roomIndex && rooms.has(a.roomIndex) && rooms.has(b.roomIndex);
  for (let i = 0; i < level.walls.length; i++) for (let j = i + 1; j < level.walls.length; j++) {
    const a = level.walls[i], b = level.walls[j];
    if (separateRooms(a, b)) continue;
    const hit = intersection(a, b);
    if (hit && ((hit.t > EPS && hit.t < 1 - EPS) || (hit.u > EPS && hit.u < 1 - EPS))) faults.push({ message: '#GeometryWallsCross', key: `cross:${key(hit.p)}` });
    if (!hit && Math.abs(cross(unit(a), unit(b))) < EPS && Math.abs(cross(sub(b.start,a.start),unit(a))) < EPS) {
      const t0 = dot(sub(b.start, a.start), unit(a)), t1 = dot(sub(b.end, a.start), unit(a));
      const lo = Math.max(0, Math.min(t0, t1)), hi = Math.min(a.length, Math.max(t0, t1));
      if (hi - lo > EPS) faults.push({ message: '#GeometryWallsOverlap', key: `overlap:${a.id}:${b.id}`,
        segment: [plus(a.start, mul(unit(a), lo)), plus(a.start, mul(unit(a), hi))] });
    }
  }
  for (const o of level.openings) {
    const w = level.walls.find(w => w.id === o.wallId);
    if (!w) faults.push({ message: '#GeometryOpeningOrphan', key: `opening:${o.id}:orphan` });
    else if (![o.width, o.height, o.offset, o.sill].every(Number.isFinite) || o.width <= 0 || o.height <= 0 || o.sill < 0) faults.push({ message: '#GeometryCheckOpeningSize', key: `opening:${o.id}:size` });
    else if (o.width > w.length - 0.04 + EPS || o.height + o.sill > w.height + EPS) faults.push({ message: '#GeometryOpeningDoesNotFit', key: `opening:${o.id}:fit` });
    else if (o.offset < o.width / 2 - EPS || o.offset > w.length - o.width / 2 + EPS) faults.push({ message: '#GeometryOpeningPastWallEnd', key: `opening:${o.id}:edge` });
  }
  for(let i=0;i<level.openings.length;i++) for(let j=i+1;j<level.openings.length;j++) {
    const a=level.openings[i],b=level.openings[j];
    const vertical=Math.min(a.sill+a.height,b.sill+b.height)-Math.max(a.sill,b.sill);
    if(a.wallId===b.wallId && vertical>EPS && Math.abs(a.offset-b.offset)<(a.width+b.width)/2+0.02-EPS) faults.push({ message: '#GeometryOpeningsOverlap', key: `openings:${[a.id, b.id].sort().join(':')}` });
  }
  return faults;
}

const knownFaults = new WeakMap<Level, Fault[]>();
const onSegment = (p: Vec2, a: Vec2, b: Vec2) => projectPoint(p, a, b).distance < EPS;

/**
 * The first rule the level breaks, or null. With `base` — the level the edit started from —
 * only what the edit itself brings in counts: an imported plan can already hold walls lying
 * over one another or crossing inside a room, and refusing every later edit for a fault the
 * user never made would leave such a plan uneditable. A fault is the same one when it sits on
 * the same geometry, so splitting a wall that carried it does not turn it into a new one.
 */
export function validateLevel(level: Level, base?: Level): string | null {
  const faults = levelFaults(level);
  if (!faults.length) return null;
  if (!base) return tr(faults[0].message);
  let known = knownFaults.get(base);
  if (!known) { known = levelFaults(base); knownFaults.set(base, known); }
  const priors = known;
  const inherited = (fault: Fault) => priors.some(prior => prior.message === fault.message
    && (fault.segment && prior.segment ? onSegment(fault.segment[0], ...prior.segment) && onSegment(fault.segment[1], ...prior.segment) : prior.key === fault.key));
  const found = faults.find(fault => !inherited(fault));
  return found ? tr(found.message) : null;
}

function simplePolygon(poly: Vec2[]): boolean {
  if (poly.length < 3 || Math.abs(polygonArea(poly)) < 0.04) return false;
  if (new Set(poly.map(key)).size !== poly.length) return false;
  for (let i = 0; i < poly.length; i++) for (let j = i + 1; j < poly.length; j++) {
    if (j === i + 1 || (i === 0 && j === poly.length - 1)) continue;
    const a = {start:poly[i],end:poly[(i+1)%poly.length]} as Wall;
    const b = {start:poly[j],end:poly[(j+1)%poly.length]} as Wall;
    if (intersection(a,b)) return false;
  }
  return true;
}

function simplify(poly: Vec2[]): Vec2[] {
  let changed = true;
  while (changed && poly.length >= 3) {
    changed = false;
    for (let i = 0; i < poly.length; i++) {
      if (samePoint(poly[i], poly[(i+1)%poly.length]) || samePoint(poly[(i+poly.length-1)%poly.length],poly[(i+1)%poly.length])) {
        poly.splice(i,1); changed = true; break;
      }
    }
  }
  return poly;
}

type Segment = Pick<Wall, 'start' | 'end'>;

function detectPolygons(walls: readonly Segment[], previous: readonly Room[]): Vec2[][] {
  const nodes = new Map<string, Vec2>();
  const links = new Map<string, Set<string>>();
  for (const w of walls) for (const [p,q] of [[w.start,w.end],[w.end,w.start]]) {
    const a=key(p), b=key(q); nodes.set(a,p); nodes.set(b,q);
    if (!links.has(a)) links.set(a,new Set());
    links.get(a)!.add(b);
  }
  const sorted = new Map([...links].map(([a,bs]) => [a,[...bs].sort((b,c) => {
    const origin=nodes.get(a)!, p=sub(nodes.get(b)!,origin), q=sub(nodes.get(c)!,origin);
    return Math.atan2(p.z,p.x)-Math.atan2(q.z,q.x);
  })]));
  const visited = new Set<string>(), polygons: Vec2[][]=[];
  for (const [a,bs] of sorted) for (const b of bs) {
    if (visited.has(`${a}>${b}`)) continue;
    let from=a, to=b, closed=false;
    const path: Vec2[]=[];
    for(let step=0;step<=walls.length*2;step++) {
      visited.add(`${from}>${to}`); path.push(nodes.get(from)!);
      const neighbours=sorted.get(to)!;
      const next=neighbours[(neighbours.indexOf(from)+neighbours.length-1)%neighbours.length];
      from=to;to=next;
      if(from===a && to===b) {closed=true;break;}
    }
    const poly=simplify(path);
    if(!closed || !simplePolygon(poly)) continue;
    const area=polygonArea(poly);
    if(area<=0)continue;
    // Unity's RoomShapeFinder recognises new rooms above 0.4 m². An imported small room remains
    // its own room, including after a collinear split or a corner edit; a newly cut sliver does not.
    if(area>0.4 || previous.some(room=>Math.abs(area-Math.abs(polygonArea(room.polygon)))<EPS
      && poly.every(p=>onEdge(room.polygon,p)) && room.polygon.every(p=>onEdge(poly,p)))) polygons.push(poly);
  }
  return polygons;
}

export function roomProbe(poly: Vec2[]): Vec2 {
  const avg=mul(poly.reduce(plus,{x:0,z:0}),1/poly.length);
  if(contains(poly,avg)) return avg;
  for(let i=0;i<poly.length;i++) {
    const p=poly[i],q=poly[(i+1)%poly.length],d=sub(q,p),len=distance(p,q);
    const mid=plus(mul(plus(p,q),0.5),mul({x:-d.z,z:d.x},0.01/len));
    if(contains(poly,mid)) return mid;
  }
  return avg;
}

/**
 * The contour as its walls now stand, or null once an edge has lost its wall. An edge stays walled
 * while the walls along it cover it end to end. Where no single wall covers it any more — the wall
 * was split where a partition was attached or a corner was put on it — the joints between the
 * covering walls become corners of the contour, as the cycle search makes them for a drawn room,
 * so the contour follows a joint that is moved later.
 */
function walledContour(walls: readonly Wall[], contour: Vec2[]): Vec2[] | null {
  const result: Vec2[] = [];
  for(let i=0;i<contour.length;i++) {
    const a=contour[i], b=contour[(i+1)%contour.length], span=distance(a,b);
    if(span<EPS) continue;
    result.push(a);
    const cover=stretches(walls,a,b);
    if(cover.some(c=>c.lo<EPS && c.hi>span-EPS)) continue;
    for(let reach=0;reach<span-EPS;) {
      const next=cover.filter(c=>c.lo<=reach+EPS).reduce<typeof cover[number]|undefined>((best,c)=>!best||c.hi>best.hi?c:best,undefined);
      if(!next || next.hi<=reach+EPS) return null;
      reach=next.hi;
      if(reach<span-EPS) result.push({...next.far});
    }
  }
  // A corner in a straight run that no wall ends at any more — the joint of a partition since taken
  // away — is dropped, as Room.SimplifyShape drops it in the app.
  for(let i=0;i<result.length && result.length>3;) {
    const p=result[i], prev=result[(i+result.length-1)%result.length], next=result[(i+1)%result.length];
    if(projectPoint(p,prev,next).distance<EPS && !walls.some(w=>samePoint(w.start,p)||samePoint(w.end,p))) result.splice(i,1);
    else i++;
  }
  return result.length>=3 && Math.abs(polygonArea(result))>EPS ? result : null;
}

/** What each wall lying along the edge a→b covers of it, in metres from `a`, with the wall's ends there. */
function stretches(walls: readonly Segment[], a: Vec2, b: Vec2): { lo: number; hi: number; near: Vec2; far: Vec2 }[] {
  const span=distance(a,b), along=mul(sub(b,a),1/span), cover: { lo: number; hi: number; near: Vec2; far: Vec2 }[]=[];
  for(const w of walls) {
    if(Math.abs(cross(sub(w.start,a),along))>=EPS || Math.abs(cross(sub(w.end,a),along))>=EPS) continue;
    const s=dot(sub(w.start,a),along), e=dot(sub(w.end,a),along);
    const lo=Math.max(0,Math.min(s,e)), hi=Math.min(span,Math.max(s,e));
    if(hi-lo>EPS) cover.push({lo,hi,near:lo<EPS?a:s<e?w.start:w.end,far:hi>span-EPS?b:s>e?w.start:w.end});
  }
  return cover;
}

/** The stretches of the edge a→b that walls still cover, joined where they touch. */
function walledStretches(walls: readonly Segment[], a: Vec2, b: Vec2): Segment[] {
  if(distance(a,b)<EPS) return [];
  const joined: { lo: number; hi: number; near: Vec2; far: Vec2 }[]=[];
  for(const c of stretches(walls,a,b).sort((x,y)=>x.lo-y.lo)) {
    const last=joined.at(-1);
    if(last && c.lo<=last.hi+EPS) { if(c.hi>last.hi) { last.hi=c.hi; last.far=c.far; } }
    else joined.push({...c});
  }
  return joined.map(c=>({start:c.near,end:c.far}));
}

/**
 * Segments ready for a face search among one room's walls, as Unity's WallFaceWalker prepares them:
 * ends within a millimetre are one point, a segment is cut where a collinear one ends, and a stretch
 * listed twice counts once — rooms from the app keep their own copies of a wall they share, each
 * with joints of its own.
 */
function planarSegments(segments: readonly Segment[]): Segment[] {
  const points: Vec2[]=[];
  const at=(p: Vec2)=>points.find(q=>samePoint(p,q)) ?? (points.push(p),p);
  const joined=segments.map(s=>({start:at(s.start),end:at(s.end)})).filter(s=>distance(s.start,s.end)>=EPS);
  const seen=new Set<string>(), result: Segment[]=[];
  for(const s of joined) {
    const span=distance(s.start,s.end), along=mul(sub(s.end,s.start),1/span);
    const cuts=joined.filter(o=>o!==s && [o.start,o.end].every(p=>Math.abs(cross(sub(p,s.start),along))<EPS))
      .flatMap(o=>[o.start,o.end]).filter(p=>{const t=dot(sub(p,s.start),along);return t>EPS && t<span-EPS;})
      .sort((p,q)=>dot(sub(p,s.start),along)-dot(sub(q,s.start),along));
    const stops=[s.start,...cuts,s.end];
    for(let i=1;i<stops.length;i++) {
      const a=stops[i-1], b=stops[i], id=[key(a),key(b)].sort().join('|');
      if(samePoint(a,b) || seen.has(id)) continue;
      seen.add(id); result.push({start:a,end:b});
    }
  }
  return result;
}

const sameOutline=(a: Vec2[], b: Vec2[]) => Math.abs(Math.abs(polygonArea(a))-Math.abs(polygonArea(b)))<EPS
  && a.every(p=>onEdge(b,p)) && b.every(p=>onEdge(a,p));

const searchedMemo = new WeakMap<Level, Set<number>>();
/**
 * The rooms of a level its cycle search finds. Every other room owns its contour, as every room does
 * in the app: an app project lists a shared wall once per room, the copies overlapping with joints of
 * their own, and the search finds few of its rooms (two of eight on the first floor of «Дом в два
 * этажа»). Such rooms are rebuilt from their own walls, as Unity's RoomContourBuilder rebuilds them.
 */
function searchedRooms(base: Level): Set<number> {
  let result=searchedMemo.get(base);
  if(result) return result;
  const polygons=detectPolygons(base.walls,base.rooms);
  result=new Set(base.rooms.filter(r=>polygons.some(p=>sameOutline(p,r.polygon))).map(r=>r.index));
  searchedMemo.set(base,result);
  return result;
}

/** The walls an edit drew or moved: those not lying along a wall of the level it started from. */
function editedWalls(base: Level, level: Level): Set<string> {
  return new Set(level.walls.filter(w=>!base.walls.some(b=>onSegment(w.start,b.start,b.end)&&onSegment(w.end,b.start,b.end))).map(w=>w.id));
}

/**
 * The pieces a room owning its contour falls into once a wall the edit drew or moved closes a new
 * outline inside it. As Unity's RebuildRoomsFromWalls, the search runs among the room's own walls —
 * its contour and the walls standing loose inside it — and only an outline through an edited wall
 * counts: walls the room already held never split it, and a piece of 0.4 m² or less is no room.
 * Largest first; none while nothing new closes.
 */
function partition(level: Level, room: Room, contour: Vec2[], previous: readonly Room[], edited: ReadonlySet<string>): Vec2[][] {
  const inside=(p: Vec2)=>contains(contour,p)||onEdge(contour,p);
  const inner=level.walls.filter(w=>[w.start,w.center,w.end].every(inside) && ![w.start,w.center,w.end].every(p=>onEdge(contour,p))
    && !previous.some(r=>r.index!==room.index && [w.start,w.center,w.end].every(p=>onEdge(r.polygon,p))));
  const drawn=inner.filter(w=>edited.has(w.id));
  if(!drawn.length) return [];
  const sides=contour.map((a,i)=>({start:a,end:contour[(i+1)%contour.length]}));
  return detectPolygons(planarSegments([...sides,...inner]),[])
    .filter(face=>!sameOutline(face,contour) && face.some((p,i)=>{
      const q=face[(i+1)%face.length];
      return drawn.some(w=>onSegment(p,w.start,w.end)&&onSegment(q,w.start,w.end));
    }))
    .sort((a,b)=>Math.abs(polygonArea(b))-Math.abs(polygonArea(a)));
}

/** Whether two rooms meet along a stretch of their contours that no wall covers any more. */
function openBetween(walls: readonly Wall[], a: Room, b: Room): boolean {
  return a.polygon.some((p,i)=>{
    const q=a.polygon[(i+1)%a.polygon.length], span=distance(p,q);
    if(span<EPS) return false;
    const along=mul(sub(q,p),1/span);
    return b.polygon.some((s,j)=>{
      const t=b.polygon[(j+1)%b.polygon.length];
      if(Math.abs(cross(sub(s,p),along))>=EPS || Math.abs(cross(sub(t,p),along))>=EPS) return false;
      const u=dot(sub(s,p),along), v=dot(sub(t,p),along), lo=Math.max(0,Math.min(u,v)), hi=Math.min(span,Math.max(u,v));
      if(hi-lo<=EPS) return false;
      const from=plus(p,mul(along,lo)), to=plus(p,mul(along,hi)), walled=walledStretches(walls,from,to);
      return !(walled.length===1 && samePoint(walled[0].start,from) && samePoint(walled[0].end,to));
    });
  });
}

/**
 * The outline rooms take together once the walls between them are gone, as Unity's WallRemoveHandler
 * merges them: the stretches of their contours still walled and the walls standing loose inside
 * them. Largest first.
 */
function mergedOutlines(level: Level, group: readonly Room[], previous: readonly Room[]): Vec2[][] {
  const sides=group.flatMap(r=>r.polygon.flatMap((a,i)=>walledStretches(level.walls,a,r.polygon[(i+1)%r.polygon.length])));
  const within=(p: Vec2)=>group.some(r=>contains(r.polygon,p)||onEdge(r.polygon,p));
  const inner=level.walls.filter(w=>[w.start,w.center,w.end].every(within) && !previous.some(r=>[w.start,w.center,w.end].every(p=>onEdge(r.polygon,p))));
  return detectPolygons(planarSegments([...sides,...inner]),group).filter(face=>group.some(r=>contains(r.polygon,roomProbe(face))))
    .sort((a,b)=>Math.abs(polygonArea(b))-Math.abs(polygonArea(a)));
}

/**
 * The rooms as the walls now stand. `previous` are the rooms before the edit, with the corners it moved
 * already moved; `base`, the level it started from, tells the rooms that own their contour (see
 * searchedRooms) and the walls the edit drew or moved.
 */
export function rebuildRooms(level: Level, previous: Room[] = level.rooms, base?: Level): void {
  const used = new Set<number>();
  let nextIndex=Math.max(-1,...previous.map(r=>r.index))+1;
  const area=(polygon: Vec2[])=>Math.abs(polygonArea(polygon));
  const searched=base?searchedRooms(base):undefined;
  const contours=new Map(searched?previous.filter(r=>!searched.has(r.index)&&r.polygon.length>=3).map(r=>[r.index,walledContour(level.walls,r.polygon)] as const):[]);
  // An outline the search finds for a room owning its contour is that room's to rebuild — a piece of
  // it, or the room joined with the one next door once the wall between them is gone. It is set aside
  // and used only if the room itself cannot be rebuilt from its walls.
  const setAside=new Map<number,Vec2[][]>();
  const found=(polygon: Vec2[], source: Room | undefined, index: number): Room => ({...source,index,name:source?.name??tr('#RoomAll'),roomType:source?.roomType??'',
    height:source?.height??level.height,...(!source ? {floorSurface:defaultFloorSurface()} : {}),polygon,area:area(polygon)});
  level.rooms=detectPolygons(level.walls,previous).flatMap(polygon => {
    const probe=roomProbe(polygon);
    const scored=previous.map(r=>({r,score:r.polygon.filter(p=>polygon.some(q=>samePoint(p,q))).length + (contains(r.polygon,probe)?100:0)})).sort((a,b)=>b.score-a.score);
    const source=scored.find(({r})=>contains(r.polygon,probe))?.r;
    if(source && contours.has(source.index)) { setAside.set(source.index,[...setAside.get(source.index)??[],polygon]); return []; }
    const index=source && !used.has(source.index) && !contours.has(source.index) ? source.index : nextIndex++;
    used.add(index);
    return [found(polygon,source,index)];
  });
  // A room owns its contour, as in the native editor, so the cycle search is a way to find rooms
  // rather than the authority on which ones exist. Two rooms laid exactly on top of one another
  // share every edge and the search can only return one of them; the other is kept here because
  // its own walls are all still on the plan. A room whose walls were deleted is not resurrected.
  const indices=new Set(level.rooms.map(r=>r.index)), edited=base?editedWalls(base,level):new Set<string>();
  const broken: Room[]=[];
  for(const room of previous) {
    if(room.polygon.length<3) continue;
    const owned=contours.has(room.index);
    if(!owned && indices.has(room.index)) continue;
    const contour=owned?contours.get(room.index):walledContour(level.walls,room.polygon);
    if(!contour) { broken.push(room); continue; }
    if(!owned) { level.rooms.push({...room,polygon:contour,area:area(contour)}); continue; }
    // A room owning its contour stays while its walls do, and a partition closing a new outline in
    // it splits it as the app splits it: the largest piece along its contour keeps the room, every
    // other piece becomes a room with its finishes, and a room drawn inside it is a new room too.
    let kept=false;
    for(const polygon of partition(level,room,contour,previous,edited)) {
      const own: boolean=!kept && polygon.some((p,i)=>onEdge(contour,mul(plus(p,polygon[(i+1)%polygon.length]),.5)));
      level.rooms.push({...room,index:own?room.index:nextIndex++,polygon,area:area(polygon)});
      kept||=own;
    }
    if(!kept) level.rooms.push({...room,polygon:contour,area:area(contour)});
  }
  // Rooms whose shared wall was removed become one, as the app merges them: the largest keeps its
  // identity and the type of the largest room that had one. A room that merges with nothing keeps
  // whatever the search found in its place.
  const groups: Room[][]=[];
  for(const room of broken) {
    const joined=groups.filter(g=>g.some(r=>openBetween(level.walls,r,room)));
    groups.push([room,...joined.flat()]);
    for(const g of joined) groups.splice(groups.indexOf(g),1);
  }
  for(const group of groups) {
    const outlines=group.length>1?mergedOutlines(level,group,previous):[];
    if(!outlines.length) {
      for(const room of group) (setAside.get(room.index)??[]).forEach((polygon,i)=>level.rooms.push(found(polygon,room,i?nextIndex++:room.index)));
      continue;
    }
    const byArea=[...group].sort((a,b)=>area(b.polygon)-area(a.polygon)), host=byArea[0], roomType=byArea.find(r=>r.roomType)?.roomType??host.roomType;
    outlines.forEach((polygon,i)=>level.rooms.push({...host,roomType,index:i?nextIndex++:host.index,polygon,area:area(polygon)}));
  }
  // A nested room owns its own floor, so its contour is subtracted from the room around it:
  // otherwise the same square metres are counted by both. Only the immediate children are
  // subtracted, so a third level of nesting is not removed twice.
  for(const room of level.rooms) {
    const nested=level.rooms.filter(r=>r!==room && polygonNested(room.polygon,r.polygon));
    const children=nested.filter(r=>!nested.some(o=>o!==r && polygonNested(o.polygon,r.polygon)));
    if(children.length) room.area=Math.max(0,room.area-children.reduce((sum,r)=>sum+Math.abs(polygonArea(r.polygon)),0));
  }
  level.outline=level.rooms.reduce<Vec2[]>((largest,r)=>Math.abs(polygonArea(r.polygon))>Math.abs(polygonArea(largest))?r.polygon:largest,[]).map(p=>({...p}));
  for(const w of level.walls) {
    // A wall sitting on a room's own contour belongs to it, whichever other rooms happen to lie
    // over that spot. Only when the wall is loose inside does the smallest room around it win,
    // so a nested room's contour is never claimed by the room enclosing it.
    const contour=level.rooms.filter(r=>[w.start,w.center,w.end].every(p=>onEdge(r.polygon,p)));
    const smallest=(list:Room[])=>list.reduce<Room|undefined>((best,r)=>
      !best||Math.abs(polygonArea(r.polygon))<Math.abs(polygonArea(best.polygon))?r:best,undefined);
    // The rooms two centimetres to either side of the centreline: `ahead` on the side the wall faces.
    const beside=(sign:1|-1)=>{const probe=plus(w.center,mul(w.normalIn,0.02*sign));return level.rooms.filter(r=>contains(r.polygon,probe));};
    const ahead=beside(1),behind=beside(-1);
    // Ownership is remembered rather than rediscovered while it still holds. Two rooms laid on top
    // of one another have walls lying on the very same line, which no amount of geometry can tell
    // apart; only the wall's own room keeps each contour whole. A stale owner is recomputed: lying
    // on a room's contour is a fact about the geometry, not about which way the wall faces, so the
    // side the wall faces only breaks the tie for a boundary two rooms share, and the tighter
    // contour wins so the room underneath never swallows the walls of the one laid over it.
    const owner=contour.find(r=>r.index===w.roomIndex)
      ??smallest(contour.filter(r=>ahead.includes(r)))??smallest(contour.filter(r=>behind.includes(r)))??smallest(contour)
      ??smallest(ahead)??smallest(behind);
    // A wall faces its own room. A drawn wall starts out facing the right-hand side of its stroke,
    // so a room drawn clockwise leaves its walls facing outdoors — or, drawn inside another room,
    // facing the room around it — until the room closes and the wall is turned round here. A wall
    // facing a neighbouring room is left as it stands: an import now and then lists a room's wall
    // with its front towards the room next door, and that is the plan's to keep, not the editor's
    // to reverse.
    if(owner && !ahead.includes(owner) && behind.includes(owner) && ahead.every(r=>behind.includes(r))) turnWall(w,level);
    w.roomIndex=owner?.index??level.rooms[0]?.index??0;
  }
}

function relocateAttachments(level: Level, old: Level): void {
  for(const o of level.openings) {
    const w=level.walls.find(w=>w.id===o.wallId), before=old.walls.find(w=>w.id===o.wallId);
    if(w && before && (w.length!==before.length || !samePoint(w.start,before.start))) o.offset=Math.max(o.width/2+0.02,Math.min(w.length-o.width/2-0.02,o.offset));
  }
  for(const item of level.items) {
    if(!item.wallId) continue;
    const w=level.walls.find(w=>w.id===item.wallId), before=old.walls.find(w=>w.id===item.wallId);
    if(!w || !before) continue;
    // Measured along start→end on both sides, whatever `dir` a wall carried in: an import sets it
    // from the authored rotation, the editor from the ends.
    const local=sub(item.position,before.center);
    const along=dot(local,unit(before)), normal=dot(local,before.normalIn)+(w.thickness-before.thickness)/2;
    item.position=plus(w.center,plus(mul(unit(w),along),mul(w.normalIn,normal)));
    item.rotation+=(w.theta-before.theta)*Math.PI/180;
  }
}

/** Split endpoint touches and coalesce exact duplicate segments as one topology change. */
/**
 * The junctions a plan already has: an endpoint lying on the body of another wall. An import can
 * leave such a junction unwelded, with no split in the host; it is the plan's, not the edit's,
 * and welding it on some later unrelated edit would refuse that edit for a stub it never made.
 */
const junctionMemo = new WeakMap<Level, Set<string>>();
function oldJunctions(base: Level): Set<string> {
  let result = junctionMemo.get(base);
  if (result) return result;
  result = new Set<string>();
  for (const host of base.walls) for (const other of base.walls) {
    if (other.id === host.id) continue;
    for (const p of [other.start, other.end]) {
      const hit = projectPoint(p, host.start, host.end);
      if (hit.distance < EPS && hit.t * host.length > EPS && (1 - hit.t) * host.length > EPS) result.add(`${host.id}|${key(p)}`);
    }
  }
  junctionMemo.set(base, result);
  return result;
}

/**
 * Walls of two rooms lying end to end on one stretch: how a project from the app lists a shared wall,
 * a copy for each room with its own finish, plinth and openings. Pairs both ways, `a|b` and `b|a`.
 */
const copyMemo = new WeakMap<Level, Set<string>>();
function roomCopies(base: Level): Set<string> {
  let result = copyMemo.get(base);
  if (result) return result;
  result = new Set<string>();
  for (const a of base.walls) for (const b of base.walls)
    if (a.id !== b.id && a.roomIndex !== b.roomIndex
      && ((samePoint(a.start, b.start) && samePoint(a.end, b.end)) || (samePoint(a.start, b.end) && samePoint(a.end, b.start)))) result.add(`${a.id}|${b.id}`);
  copyMemo.set(base, result);
  return result;
}

/** Import copies agree to a centimetre or two: the copies that take a wall's thickness and height with it. */
const COPY_REACH = .01;
function wallCopyIds(base: Level, id: string): string[] {
  const wall = base.walls.find(w => w.id === id);
  if (!wall) return [];
  const near = (a: Vec2, b: Vec2) => distance(a, b) < COPY_REACH;
  return base.walls.filter(other => other.id !== id && other.roomIndex !== wall.roomIndex
    && ((near(other.start, wall.start) && near(other.end, wall.end)) || (near(other.start, wall.end) && near(other.end, wall.start)))).map(other => other.id);
}

function connectEndpoints(level: Level, changed: Set<string>, allowCollapse: boolean, stableIds = false, movingRoom?: Room, old?: Set<string>, moved: Vec2[] = [],
  preserveEndpoint?: {wallId:string;end:'start'|'end'}, contourDrag=false, apart?: ReadonlySet<string>): string | null {
  const stableSplits=stableIds || !!preserveEndpoint;
  // Rooms whose floors intersect are stacked, not joined. Their walls never weld into one another:
  // cutting a neighbour's wall because a corner of the room laid over it happens to land there
  // refuses the drag outright when the cut falls in a doorway, and otherwise tears the contour up.
  const stackedRooms = new Set<string>();
  for (let i = 0; i < level.rooms.length; i++) for (let j = i + 1; j < level.rooms.length; j++) {
    const a = level.rooms[i], b = level.rooms[j];
    if (!roomsOverlap(a.polygon, b.polygon)) continue;
    stackedRooms.add(`${a.index}:${b.index}`); stackedRooms.add(`${b.index}:${a.index}`);
  }
  const stacked = (a: Wall, b: Wall) => a.roomIndex !== b.roomIndex && stackedRooms.has(`${a.roomIndex}:${b.roomIndex}`);
  const collapsed=level.walls.filter(w=>w.length<EPS);
  if(collapsed.length) {
    if(!allowCollapse)return tr('#GeometryWallTooShort');
    const ids=new Set(collapsed.map(w=>w.id));
    if(level.openings.some(o=>ids.has(o.wallId)) || level.items.some(i=>i.wallId && ids.has(i.wallId))) return tr('#GeometryCornerMergeBlocked');
    level.walls=level.walls.filter(w=>!ids.has(w.id));
  }
  const snapshot=[...level.walls];
  // Which wall each piece a cut makes was cut from, so a copy cut in two still knows its pair.
  const origin=new Map<string,string>();
  for(const host of snapshot) {
    const cuts:number[]=[];
    for(const other of snapshot) {
      // Only a junction the edit touches is welded: an imported plan can hold a wall ending on
      // another's body with no split there, and cutting that host on every unrelated edit would
      // refuse the edit for a stub or an opening the user never went near.
      if(other.id===host.id||stacked(host,other)||(!changed.has(host.id)&&!changed.has(other.id)))continue;
      for(const p of [other.start,other.end]) {
        if(old?.has(`${host.id}|${key(p)}`))continue;
        const hit=projectPoint(p,host.start,host.end);
        if(hit.distance<EPS && hit.t*host.length>EPS && (1-hit.t)*host.length>EPS)cuts.push(hit.t*host.length);
      }
    }
    const before=new Set(level.walls.map(w=>w.id));
    const error=splitSegments(level,host,cuts,stableSplits,host.id===preserveEndpoint?.wallId?preserveEndpoint.end:'start',contourDrag?EPS:MIN_WALL);if(error)return error;
    for(const w of level.walls)if(!before.has(w.id))origin.set(w.id,host.id);
    if (stableSplits && changed.has(host.id)) for (const w of level.walls) if (w.id.startsWith(`${host.id}:split:`)) changed.add(w.id);
  }
  const copies=(a:Wall,b:Wall)=>!!apart?.has(`${origin.get(a.id)??a.id}|${origin.get(b.id)??b.id}`);
  // Prefer the stationary host's identity and finish when a dragged wall lands on it.
  const order=new Map(level.walls.map((wall,index)=>[wall.id,index]));
  const candidates=[...level.walls].sort((a,b)=>Number(changed.has(a.id))-Number(changed.has(b.id)));
  const unique:Wall[]=[];
  for(const wall of candidates) {
    // Two identical walls of one room are always one wall too many. Coincident copies held by
    // different rooms — how an import lists a shared wall, each with its own openings — stay as
    // they are unless the edit moved one of them: merging them behind the user's back would stack
    // those openings on one wall and refuse the edit for it. With `apart`, the copies the level
    // already held stay two even when the edit moves them: a wall has one plinth and its front
    // finish, so the copy that went would take its room's finish and plinth with it.
    const keep=unique.find(w=>!stacked(w,wall)&&!copies(w,wall)&&(changed.has(w.id)||changed.has(wall.id)||w.roomIndex===wall.roomIndex)
      && ((samePoint(w.start,wall.start)&&samePoint(w.end,wall.end)) || (samePoint(w.start,wall.end)&&samePoint(w.end,wall.start))));
    if(!keep){unique.push(wall);continue;}
    if (stableIds) {
      const inward = changed.has(wall.id) ? movingRoom ?? (contourDrag ? level.rooms.find(r=>r.index===wall.roomIndex) : undefined) : undefined;
      if (inward) {
        const front = contains(inward.polygon, plus(wall.center, mul(wall.normalIn, .02)));
        const surface = front ? wall.frontSurface : wall.backSurface;
        const targetFront = dot(wall.normalIn, keep.normalIn) * (front ? 1 : -1) > 0;
        if (surface) keep[targetFront ? 'frontSurface' : 'backSurface'] = surface;
      }
    }
    for(const opening of level.openings.filter(o=>o.wallId===wall.id)) {
      const centre=plus(wall.start,mul(unit(wall),opening.offset));
      opening.offset=dot(sub(centre,keep.start),unit(keep));
      if(dot(wall.normalIn,keep.normalIn)<0)opening.flipped=!opening.flipped;
      if (stableIds) {
        const duplicate = level.openings.find(o => o.wallId === keep.id && Math.abs(o.offset - opening.offset) < EPS
          && Math.abs(o.width - opening.width) < EPS && Math.abs(o.height - opening.height) < EPS
          && Math.abs(o.sill - opening.sill) < EPS && o.kind === opening.kind
          && (!!o.kind || o.modelRef.kind === opening.modelRef.kind && o.modelRef.id === opening.modelRef.id));
        if (duplicate) { level.openings = level.openings.filter(o => o !== opening); continue; }
      }
      opening.wallId=keep.id;
    }
    for(const item of level.items)if(item.wallId===wall.id)item.wallId=keep.id;
  }
  level.walls=unique.sort((a,b)=>order.get(a.id)!-order.get(b.id)!);
  healSeams(level, moved);
  return null;
}

/**
 * Rejoin the two halves of a wall that a since-departed neighbour had cut in two. A partial join
 * splits the wall it lands on, and moving the room away used to leave the cut behind as a seam the
 * plan still draws. Only a joint where nothing else attaches and both halves match is healed, so a
 * corner, a branch, or a change of finish is left exactly as authored.
 */
function healSeams(level: Level, moved: Vec2[] = []): void {
  // A corner the user is moving is theirs to keep, even when it lands in line with its neighbours:
  // straightening a wall shows it straight, and only deleting the corner makes the two walls one.
  const held = new Set(moved.map(key));
  // Halves cut from one wall can be stored wound opposite ways, so their faces are compared with
  // that flip taken out: the same room finish written on both is still the same wall.
  const faces = (w: Wall, flip: boolean) => JSON.stringify(flip
    ? [w.backSurface ?? null, w.frontSurface ?? null, w.plinth ?? null]
    : [w.frontSurface ?? null, w.backSurface ?? null, w.plinth ?? null]);
  for (let pass = 0; pass < level.walls.length; pass++) {
    const ends = new Map<string, Wall[]>();
    for (const w of level.walls) for (const p of [w.start, w.end]) {
      const k = key(p); if (!ends.has(k)) ends.set(k, []); ends.get(k)!.push(w);
    }
    const halves = (a: Wall, b: Wall) => a.id !== b.id && a.roomIndex === b.roomIndex
      && Math.abs(a.thickness - b.thickness) < EPS && Math.abs(a.height - b.height) < EPS
      && Math.abs(cross(unit(a), unit(b))) < EPS
      && faces(a, false) === faces(b, dot(a.normalIn, b.normalIn) < 0);
    const pair = [...ends.entries()].filter(([k]) => !held.has(k)).flatMap(([, walls]): Wall[][] => {
      if (walls.length === 2) return [walls];
      // Rooms from the app keep their own copy of a wall they share, so a departed partition had cut
      // every room's copy at the same joint, and Room.SimplifyShape joins them again in the app. With
      // nothing across the line, each room's two halves there, one to either side, are a pair.
      if (!walls.every(w => Math.abs(cross(unit(w), unit(walls[0]))) < EPS)) return [];
      const rooms = new Map<number, Wall[]>();
      for (const w of walls) rooms.set(w.roomIndex, [...rooms.get(w.roomIndex) ?? [], w]);
      return [...rooms.values()].filter(([a, b]) => {
        if (!b || rooms.get(a.roomIndex)!.length !== 2) return false;
        const joint = [a.start, a.end].find(p => samePoint(p, b.start) || samePoint(p, b.end));
        const far = (w: Wall) => samePoint(w.start, joint!) ? w.end : w.start;
        return !!joint && dot(sub(far(a), joint), sub(far(b), joint)) < 0;
      });
    }).find(([a, b]) => halves(a, b));
    if (!pair) return;
    const [keep, drop] = pair;
    // Fix every opening's position in the plan first, then re-measure it along the healed wall:
    // the merged wall may start from the other end, and an offset read against the old start
    // would slide the opening down the wall.
    const centres = new Map<string, Vec2>();
    for (const o of level.openings) {
      const host = o.wallId === keep.id ? keep : o.wallId === drop.id ? drop : null;
      if (host) centres.set(o.id, plus(host.start, mul(unit(host), o.offset)));
    }
    const joint = [drop.start, drop.end].find(p => samePoint(p, keep.start) || samePoint(p, keep.end))!;
    const far = samePoint(drop.start, joint) ? drop.end : drop.start;
    if (samePoint(keep.start, joint)) keep.start = far; else keep.end = far;
    refreshWall(keep);
    for (const o of level.openings) {
      if (o.wallId !== keep.id && o.wallId !== drop.id) continue;
      if (o.wallId === drop.id && dot(drop.normalIn, keep.normalIn) < 0) o.flipped = !o.flipped;
      o.wallId = keep.id;
      o.offset = dot(sub(centres.get(o.id)!, keep.start), unit(keep));
    }
    for (const item of level.items) if (item.wallId === drop.id) item.wallId = keep.id;
    level.walls = level.walls.filter(w => w.id !== drop.id);
  }
}

function isContourWall(level: Level, wall: Wall): boolean {
  return level.rooms.some(room=>[wall.start,wall.center,wall.end].every(p=>onEdge(room.polygon,p)));
}

function replaceEndpoints(base: Level, replacements: Array<{from:Vec2;to:Vec2}>, properties?: {id:string;values:WallProperties}, allowCollapse=false,
  freeEndpoint?: {wallId:string;end:'start'|'end'}): EditResult {
  const level=structuredClone(base);
  const changed=new Set(base.walls.filter(w=>replacements.some(r=>samePoint(r.from,w.start)||samePoint(r.from,w.end))).map(w=>w.id));
  const replace=(p:Vec2)=>snapPrecision(replacements.find(r=>samePoint(r.from,p))?.to??p);
  // A shared wall is one wall to the user: its thickness and height go to every room's copy of it,
  // as hiding and splitting it already do.
  const edited=properties?new Set([properties.id,...wallCopyIds(base,properties.id)]):null;
  for(const w of level.walls) {
    w.start=replace(w.start);w.end=replace(w.end);
    if(properties&&edited?.has(w.id)) {
      w.thickness=properties.values.thickness??w.thickness;
      w.height=properties.values.height??w.height;
    }
    refreshWall(w);
  }
  const previous=base.rooms.map(r=>({...r,polygon:r.polygon.map(replace)}));
  if(!allowCollapse && previous.some((r,i)=>!simplePolygon(r.polygon) || polygonArea(r.polygon)*polygonArea(base.rooms[i].polygon)<=0)) return fail(tr('#GeometryCornerBreaksRoom'));
  relocateAttachments(level,base);
  if(freeEndpoint) {
    const error=splitFreeEndpointCrossings(level,base,freeEndpoint,changed);if(error)return fail(error);
  }
  const connectionError=connectEndpoints(level,changed,allowCollapse,false,undefined,oldJunctions(base),replacements.map(r=>snapPrecision(r.to)),freeEndpoint,false,roomCopies(base));if(connectionError)return fail(connectionError);
  collapseTwinOpenings(level,base);
  const error=validateLevel(level,base); if(error) return fail(error);
  rebuildRooms(level,previous,base);
  if(base.rooms.length && !level.rooms.length)return fail(tr('#GeometryMoveCollapsesAllRooms'));
  return {level};
}

/**
 * An import lists the door between two rooms on each room's copy of their shared wall. When an
 * edit brings the copies onto one wall, two openings that stood on the same spot before the edit
 * are one opening — the copy measured from a moved end may have drifted with it, so the one that
 * stayed put is kept. Two openings the edit itself brought together are still a clash.
 */
function collapseTwinOpenings(level: Level, base: Level): void {
  const centreIn = (source: Level, id: string): Vec2 | null => {
    const o = source.openings.find(o => o.id === id), w = o && source.walls.find(w => w.id === o.wallId);
    return o && w ? plus(w.start, mul(unit(w), o.offset)) : null;
  };
  const alike = (a: Opening, b: Opening) => a.wallId === b.wallId && Math.abs(a.width - b.width) < .03
    && Math.abs(a.height - b.height) < .03 && Math.abs(a.sill - b.sill) < .03 && (a.kind ?? '') === (b.kind ?? '');
  const coincided = (a: Opening, b: Opening) => { const p = centreIn(base, a.id), q = centreIn(base, b.id); return !!p && !!q && distance(p, q) < .03; };
  const drift = (o: Opening) => { const was = centreIn(base, o.id), now = centreIn(level, o.id); return was && now ? distance(was, now) : Infinity; };
  const kept: Opening[] = [];
  for (const opening of level.openings) {
    const index = kept.findIndex(o => alike(o, opening) && coincided(o, opening));
    if (index < 0) kept.push(opening);
    else if (drift(opening) < drift(kept[index])) kept[index] = opening;
  }
  if (kept.length !== level.openings.length) level.openings = kept;
}

function resolveConnection(base:Level,connection:VertexSnapTarget):Vec2|null {
  const host=base.walls.find(w=>w.id===connection.wallId);
  if(!host || !finite(connection.point))return null;
  if(connection.kind==='vertex')return {...host[connection.end]};
  const hit=projectPoint(connection.point,host.start,host.end);
  return hit.distance<EPS ? hit.point : null;
}

export function moveVertex(base: Level, wallId: string, end: 'start'|'end', target: Vec2, connection?:VertexSnapTarget|null): EditResult {
  const w=base.walls.find(w=>w.id===wallId);
  const point=connection?resolveConnection(base,connection):target;
  if(!point)return fail(tr('#GeometryConnectionTargetGone'));
  if(!w)return fail(tr('#GeometryWallNotFound'));
  // Native free-end movement can cut separate walls. Connected corners, room contours and a
  // wall translated by its centre keep their own validation rules.
  const free=!isContourWall(base,w) && base.walls.filter(other=>onSegment(w[end],other.start,other.end)).length===1;
  return replaceEndpoints(base,[{from:w[end],to:point}],undefined,!!connection,free?{wallId,end}:undefined);
}

/** The visible piece between junctions. Merely picking it must not split the document. */
export function wallDragSegment(base: Level, id: string, at: Vec2): Wall | undefined {
  const wall=base.walls.find(w=>w.id===id);
  if(!wall || !isContourWall(base,wall))return wall;
  const owner=base.rooms.find(r=>r.index===wall.roomIndex);
  const stops=[0,wall.length];
  for(const other of base.walls) {
    if(other.id===id || owner&&other.roomIndex!==owner.index&&base.rooms.some(r=>r.index===other.roomIndex&&roomsOverlap(owner.polygon,r.polygon)))continue;
    for(const p of [other.start,other.end]) {
      const hit=projectPoint(p,wall.start,wall.end);
      if(hit.distance<EPS)stops.push(hit.t*wall.length);
    }
  }
  const along=projectPoint(at,wall.start,wall.end).t*wall.length;
  const cuts=stops.sort((a,b)=>a-b).filter((v,i,a)=>i===0||v-a[i-1]>EPS);
  const next=cuts.findIndex(v=>v>=along+EPS),index=next<0?cuts.length-1:Math.max(1,next);
  const piece={...wall,start:plus(wall.start,mul(unit(wall),cuts[index-1])),end:plus(wall.start,mul(unit(wall),cuts[index]))};
  refreshWall(piece);return piece;
}

export function moveWall(base: Level, id: string, delta: Vec2, connections:EndpointConnection[]=[], segment?: Pick<Wall,'start'|'end'>): EditResult {
  const source=base.walls.find(w=>w.id===id);
  const w=source && {...source,...segment};
  if(!w)return fail(tr('#GeometryWallNotFound'));
  if(segment&&(!finite(segment.start)||!finite(segment.end)||!onSegment(segment.start,source!.start,source!.end)
    ||!onSegment(segment.end,source!.start,source!.end)||distance(segment.start,segment.end)<EPS))return fail(tr('#GeometryWallSegmentGone'));
  refreshWall(w);
  let move=delta;
  for(let i=0;i<connections.length;i++) {
    const connection=connections[i],point=resolveConnection(base,connection.target);
    if(!point)return fail(tr('#GeometryConnectionTargetGone'));
    const next=sub(point,w[connection.end]);
    if(i && distance(next,move)>EPS)return fail(tr('#GeometryWallEndsCantConnect'));
    move=next;
  }
  if(!finite(move))return fail(tr('#GeometryEnterFiniteSizes'));
  if(isContourWall(base,w))return moveContourWall(base,w,move,!!connections.length);
  return replaceEndpoints(base,[{from:w.start,to:plus(w.start,move)},{from:w.end,to:plus(w.end,move)}],undefined,!!connections.length);
}

/**
 * A centre drag changes the boundary between rooms. A collinear continuation holds its junction
 * still (Unity CentralWallDragStrategy.IsJunctionAnchored); a new return connects it to the moved
 * piece. Otherwise attached ends follow. Work only on this gesture's immutable starting level.
 */
function moveContourWall(base: Level, span: Wall, delta: Vec2, allowCollapse: boolean): EditResult {
  if(distance(delta,{x:0,z:0})<EPS)return {level:structuredClone(base)};
  const level=structuredClone(base), direction=unit(span);
  const sourceRoom=base.rooms.find(r=>r.index===span.roomIndex);
  const separate=(w:Wall)=>w.roomIndex!==span.roomIndex && sourceRoom && base.rooms.some(r=>r.index===w.roomIndex && roomsOverlap(sourceRoom.polygon,r.polygon));
  const collinear=(w:Wall)=>Math.abs(cross(unit(w),direction))<EPS && Math.abs(cross(sub(w.start,span.start),direction))<EPS;
  const inSpan=(p:Vec2)=>onSegment(p,span.start,span.end);
  const moving=new Set<string>();
  for(const wall of [...level.walls]) {
    if(separate(wall)||!collinear(wall))continue;
    const cuts=[span.start,span.end].map(p=>dot(sub(p,wall.start),unit(wall))).filter(v=>v>EPS&&v<wall.length-EPS);
    const overlap=projectPoint(wall.center,span.start,span.end).distance<EPS || inSpan(wall.start)||inSpan(wall.end)
      || onSegment(span.center,wall.start,wall.end);
    if(!overlap)continue;
    // Keep the picked wall's id on the moving piece, including a middle piece of an imported wall.
    const keepAt=wall.id===span.id?dot(sub(span.center,wall.start),unit(wall)):'start';
    const error=splitSegments(level,wall,cuts,true,keepAt,EPS);if(error)return fail(error);
  }
  for(const wall of level.walls)if(!separate(wall)&&collinear(wall)&&inSpan(wall.start)&&inSpan(wall.end))moving.add(wall.id);
  const prepared=structuredClone(level);
  const points=level.walls.filter(w=>moving.has(w.id)).flatMap(w=>[w.start,w.end]);
  for(const wall of level.walls)if(!separate(wall))for(const p of [wall.start,wall.end])if(inSpan(p))points.push(p);
  const joints=points.filter((p,i)=>points.findIndex(q=>samePoint(p,q))===i).map(from=>({from,to:snapPrecision(plus(from,delta)),
    anchored:level.walls.some(w=>!moving.has(w.id)&&!separate(w)&&collinear(w)&&(samePoint(w.start,from)||samePoint(w.end,from)))}));
  const replace=(p:Vec2)=>joints.find(j=>!j.anchored&&samePoint(j.from,p))?.to??p;
  const changed=new Set(moving);
  for(const wall of level.walls) {
    if(separate(wall))continue;
    const start=moving.has(wall.id)?snapPrecision(plus(wall.start,delta)):replace(wall.start);
    const end=moving.has(wall.id)?snapPrecision(plus(wall.end,delta)):replace(wall.end);
    if(!samePoint(start,wall.start)||!samePoint(end,wall.end))changed.add(wall.id);
    wall.start=start;wall.end=end;refreshWall(wall);
  }
  // Predict each room's contour independently: the stationary side of an anchored junction
  // keeps the old corner, while the room beside the moving span gets the new return.
  const previous=base.rooms.map(room=>{
    if(sourceRoom&&room.index!==sourceRoom.index&&roomsOverlap(sourceRoom.polygon,room.polygon))return room;
    const polygon:Vec2[]=[];
    for(let i=0;i<room.polygon.length;i++) {
      const a=room.polygon[i],b=room.polygon[(i+1)%room.polygon.length];
      const cuts=[{point:a,t:0},{point:b,t:1},...joints.map(j=>projectPoint(j.from,a,b)).filter(h=>h.distance<EPS&&h.t>EPS&&h.t<1-EPS)]
        .sort((a,b)=>a.t-b.t).filter((h,i,all)=>i===0||!samePoint(h.point,all[i-1].point));
      for(let j=1;j<cuts.length;j++) {
        const p=cuts[j-1].point,q=cuts[j].point,move=inSpan(p)&&inSpan(q);
        polygon.push(move?snapPrecision(plus(p,delta)):replace(p),move?snapPrecision(plus(q,delta)):replace(q));
      }
    }
    // Inward drags can retrace an existing perpendicular edge; discard that redundant turn.
    let clean=polygon.filter((p,i)=>!samePoint(p,polygon[(i+polygon.length-1)%polygon.length]));
    for(let pass=0;pass<polygon.length;pass++) {
      const i=clean.findIndex((p,i)=>Math.abs(cross(sub(p,clean[(i+clean.length-1)%clean.length]),sub(clean[(i+1)%clean.length],p)))<1e-8);
      if(i<0||clean.length<=3)break;clean=clean.filter((_,j)=>j!==i);
    }
    return {...room,polygon:clean};
  });
  if(previous.some((room,i)=>!simplePolygon(room.polygon)||polygonArea(room.polygon)*polygonArea(base.rooms[i].polygon)<=0))return fail(tr('#GeometryMoveBreaksRoom'));
  relocateAttachments(level,prepared);
  // Stretching a neighbouring straight wall must not slide its doors and mounted furniture.
  for(const before of prepared.walls) {
    const wall=level.walls.find(w=>w.id===before.id);
    if(!wall||moving.has(wall.id)||Math.abs(cross(unit(before),unit(wall)))>EPS)continue;
    for(const o of level.openings.filter(o=>o.wallId===wall.id)) {
      const original=prepared.openings.find(v=>v.id===o.id)!;
      const centre=plus(before.start,mul(unit(before),original.offset));
      o.offset=Math.max(o.width/2+.02,Math.min(wall.length-o.width/2-.02,dot(sub(centre,wall.start),unit(wall))));
    }
    for(const item of level.items.filter(v=>v.wallId===wall.id)) {
      const original=prepared.items.find(v=>v.id===item.id)!;
      const along=Math.max(0,Math.min(wall.length,dot(sub(original.position,wall.start),unit(wall))));
      item.position=plus(plus(wall.start,mul(unit(wall),along)),mul(wall.normalIn,dot(sub(original.position,before.start),before.normalIn)));
    }
  }
  for(const [index,joint] of joints.entries()) {
    if(!joint.anchored)continue;
    const returnWall={...span,id:availableId(level,`${span.id}:return:${index}`),start:joint.from,end:joint.to};refreshWall(returnWall);
    const cuts=[0,returnWall.length],covered:Array<[number,number]>=[];
    for(const wall of level.walls) {
      if(Math.abs(cross(unit(wall),unit(returnWall)))>EPS||Math.abs(cross(sub(wall.start,returnWall.start),unit(returnWall)))>EPS)continue;
      const a=dot(sub(wall.start,returnWall.start),unit(returnWall)),b=dot(sub(wall.end,returnWall.start),unit(returnWall));
      const lo=Math.max(0,Math.min(a,b)),hi=Math.min(returnWall.length,Math.max(a,b));
      if(hi>lo+EPS){covered.push([lo,hi]);cuts.push(lo,hi);}
    }
    const stops=cuts.sort((a,b)=>a-b).filter((v,i,a)=>i===0||v-a[i-1]>EPS);
    for(let i=1;i<stops.length;i++) {
      const mid=(stops[i-1]+stops[i])/2;if(covered.some(([a,b])=>mid>=a-EPS&&mid<=b+EPS))continue;
      const piece={...returnWall,id:availableId(level,returnWall.id),start:plus(joint.from,mul(unit(returnWall),stops[i-1])),end:plus(joint.from,mul(unit(returnWall),stops[i]))};
      refreshWall(piece);level.walls.push(piece);changed.add(piece.id);
    }
  }
  level.rooms=previous;
  const error=connectEndpoints(level,changed,allowCollapse,true,undefined,oldJunctions(prepared),joints.map(j=>j.to),undefined,true);if(error)return fail(error);
  collapseTwinOpenings(level,base);
  const invalid=validateLevel(level,base);if(invalid)return fail(invalid);
  rebuildRooms(level,previous,base);
  if(level.rooms.length<base.rooms.length)return fail(tr('#GeometryMoveCollapsesRoom'));
  return {level};
}

/**
 * Moves corners of the plan, every wall end standing on them with them, as one edit — the sides of a room moved to a
 * typed size, all of it scaled, its walls thickened outward. Rooms, junctions and attachments follow as for a drag.
 */
export function moveCorners(base: Level, moves: Array<{from:Vec2;to:Vec2}>): EditResult {
  if(!moves.length)return {level:base};
  if(moves.some(move=>!finite(move.from)||!finite(move.to)))return fail(tr('#GeometryCheckSizes'));
  return replaceEndpoints(base,moves);
}

export function applyWallProperties(base: Level, id: string, values: WallProperties): EditResult {
  const w=base.walls.find(w=>w.id===id); if(!w) return fail(tr('#GeometryWallNotFound'));
  const start=values.start??w.start;
  const end=values.end??(values.length!==undefined?plus(start,mul(unit(w),values.length)):w.end);
  return replaceEndpoints(base,[{from:w.start,to:start},{from:w.end,to:end}],{id,values});
}

function newWall(a:Vec2,b:Vec2,height:number,thickness=DEFAULT_WALL_THICKNESS,surface?:Surface): Wall {
  const w:Wall={id:`w_${crypto.randomUUID()}`,start:snapPrecision(a),end:snapPrecision(b),length:0,center:{x:0,z:0},
    theta:0,dir:{x:1,z:0},normalIn:{x:0,z:1},forward:true,thickness,height,hidden:false,roomIndex:0,
    ...(surface?{frontSurface:structuredClone(surface),backSurface:structuredClone(surface)}:{})};
  refreshWall(w);return w;
}

function splitSegments(level: Level, wall: Wall, cuts: number[], stableIds = false, keepIdAt: 'start'|'end'|number = 'start', minimum=MIN_WALL): string | null {
  const direction=unit(wall);
  const stops=[0,...cuts.filter(v=>v>EPS && v<wall.length-EPS),wall.length].sort((a,b)=>a-b).filter((v,i,a)=>i===0||v-a[i-1]>EPS);
  if(stops.length===2) return null;
  const openings=level.openings.filter(o=>o.wallId===wall.id);
  for(const cut of stops.slice(1,-1)) if(openings.some(o=>Math.abs(o.offset-cut)<o.width/2+0.02)) return tr('#GeometryNewWallThroughOpening');
  const pieces:Wall[]=[];
  for(let i=1;i<stops.length;i++) {
    if(stops[i]-stops[i-1]<minimum-EPS) return tr('#GeometryJunctionLeavesShortWall');
    const retainsId=typeof keepIdAt==='number'?keepIdAt>=stops[i-1]&&keepIdAt<stops[i]:i===(keepIdAt==='end'?stops.length-1:1);
    const piece={...wall,id:retainsId?wall.id:stableIds?availableId(level, `${wall.id}:split:${Math.round(stops[i-1]*1e4)}`):`w_${crypto.randomUUID()}`,start:plus(wall.start,mul(direction,stops[i-1])),end:plus(wall.start,mul(direction,stops[i]))};
    refreshWall(piece);pieces.push(piece);
  }
  for(const o of openings) {
    const i=Math.max(0,stops.findIndex((v,i)=>i>0 && o.offset<=v)-1);
    o.wallId=pieces[i].id;o.offset-=stops[i];
  }
  for(const item of level.items.filter(i=>i.wallId===wall.id)) {
    const along=dot(sub(item.position,wall.start),direction);
    const i=Math.min(pieces.length-1,Math.max(0,stops.findIndex((v,i)=>i>0 && along<=v)-1));
    item.wallId=pieces[i].id;
  }
  level.walls=level.walls.flatMap(w=>w.id===wall.id?pieces:[w]);
  return null;
}

/** New interior crossings made by one loose free endpoint, preserving the selected tip's id. */
function splitFreeEndpointCrossings(level: Level, base: Level, moved: {wallId:string;end:'start'|'end'}, changed: Set<string>): string | null {
  const before=base.walls.find(w=>w.id===moved.wallId)!, candidate=level.walls.find(w=>w.id===moved.wallId)!;
  const interior=(hit:{t:number;u:number})=>hit.t>EPS && hit.t<1-EPS && hit.u>EPS && hit.u<1-EPS;
  const cuts:number[]=[];
  for(const host of [...level.walls]) {
    if(host.id===candidate.id || isContourWall(base,host)) continue;
    const hit=intersection(candidate,host);
    if(!hit || !interior(hit)) continue;
    const old=intersection(before,host);
    if(old && interior(old) && samePoint(old.p,hit.p)) continue;
    const error=splitSegments(level,host,[hit.u*host.length],true);if(error)return error;
    cuts.push(hit.t*candidate.length);
  }
  if(!cuts.length)return null;
  const existingIds=new Set(level.walls.map(w=>w.id));
  const error=splitSegments(level,candidate,cuts,true,moved.end);if(error)return error;
  for(const wall of level.walls)if(!existingIds.has(wall.id))changed.add(wall.id);
  return null;
}

/**
 * A wall from `a` to `b`, split where it crosses walls and left out where it lies on one. It stands as high as the floor
 * unless `height` says otherwise; `surface` finishes both its faces, as the app's Wall(size, material) does.
 */
export function addWall(base: Level, a: Vec2, b: Vec2, thickness = DEFAULT_WALL_THICKNESS, options: {reuseCovered?:boolean;height?:number;surface?:Surface} = {}): EditResult {
  if(!finite(a)||!finite(b)||distance(a,b)<MIN_WALL) return fail(tr('#GeometryDrawLongerWall'));
  const height=options.height??base.height;
  const level=structuredClone(base), candidate=newWall(a,b,height,thickness), cuts:number[]=[];
  const covered:Array<[number,number]>=[];
  for(const w of [...level.walls]) {
    const hit=intersection(candidate,w);
    if(hit) {
      cuts.push(hit.t*candidate.length);
      const error=splitSegments(level,w,[hit.u*w.length]);if(error)return fail(error);
    } else if(Math.abs(cross(unit(candidate),unit(w)))<EPS && Math.abs(cross(sub(w.start,a),unit(candidate)))<EPS) {
      const p=dot(sub(w.start,a),unit(candidate)),q=dot(sub(w.end,a),unit(candidate));
      const lo=Math.max(0,Math.min(p,q)),hi=Math.min(candidate.length,Math.max(p,q));
      if(hi>lo+EPS) {covered.push([lo,hi]);cuts.push(lo,hi);}
    }
  }
  const stops=[0,...cuts,candidate.length].sort((a,b)=>a-b).filter((v,i,arr)=>i===0||v-arr[i-1]>EPS);
  let added=false;
  for(let i=1;i<stops.length;i++) {
    const lo=stops[i-1],hi=stops[i],mid=(lo+hi)/2;
    if(covered.some(([a,b])=>mid>=a-EPS&&mid<=b+EPS)) continue;
    if(hi-lo<MIN_WALL-EPS) return fail(tr('#GeometryJunctionLeavesShortWall'));
    level.walls.push(newWall(plus(a,mul(unit(candidate),lo)),plus(a,mul(unit(candidate),hi)),height,thickness,options.surface));
    added=true;
  }
  if(!added && !options.reuseCovered)return fail(tr('#GeometryWallAlreadyHere'));
  const error=validateLevel(level,base);if(error)return fail(error);
  rebuildRooms(level,base.rooms,base);return {level};
}

/**
 * Put a corner on a wall: it becomes two walls meeting `along` metres from its start, the first
 * keeping the wall's id (so the new corner is that wall's `end`), openings and wall furniture
 * following the piece they stand on.
 */
export function splitWall(base: Level, id: string, along: number): EditResult {
  const level=structuredClone(base), wall=level.walls.find(w=>w.id===id);
  if(!wall)return fail(tr('#GeometryWallNotFound'));
  const error=splitSegments(level,wall,[along]);if(error)return fail(error);
  if(level.walls.length===base.walls.length)return fail(tr('#GeometryNoPlaceForPoint'));
  rebuildRooms(level,base.rooms);return {level};
}

/**
 * Take a corner out of the plan — the inverse of putting one on a wall. Two walls meeting there
 * become one wall from far end to far end, with their openings and wall furniture measured again
 * along it; a lone wall's endpoint takes the wall with it; a junction of three or more walls
 * stays, since which of them the user meant to keep is not knowable.
 */
export function removeVertex(base: Level, wallId: string, end: 'start' | 'end'): EditResult {
  const wall=base.walls.find(w=>w.id===wallId);
  if(!wall)return fail(tr('#GeometryWallNotFound'));
  const point=wall[end], meeting=base.walls.filter(w=>samePoint(w.start,point)||samePoint(w.end,point));
  if(meeting.length>2)return fail(tr('#GeometryJunctionPointUndeletable'));
  if(meeting.length<2)return removeWall(base,wall.id);
  const [a,b]=meeting, level=structuredClone(base);
  const merged=level.walls.find(w=>w.id===a.id)!;
  merged.start=samePoint(a.start,point)?{...a.end}:{...a.start};merged.end=samePoint(b.start,point)?{...b.end}:{...b.start};
  refreshWall(merged);
  if(merged.length<MIN_WALL-EPS)return fail(tr('#GeometryWallTooShort'));
  for(const opening of level.openings) {
    const host=opening.wallId===a.id?a:opening.wallId===b.id?b:null;
    if(!host)continue;
    const centre=plus(host.start,mul(unit(host),opening.offset)), hit=projectPoint(centre,merged.start,merged.end);
    if(dot(host.normalIn,merged.normalIn)<0)opening.flipped=!opening.flipped;
    opening.wallId=merged.id;opening.offset=Math.round(hit.t*merged.length*1e4)/1e4;
  }
  for(const item of level.items)if(item.wallId===b.id)item.wallId=merged.id;
  level.walls=level.walls.filter(w=>w.id!==b.id);
  const error=validateLevel(level,base);if(error)return fail(error);
  rebuildRooms(level,base.rooms);return {level};
}

export function removeWall(base: Level, id: string): EditResult {
  const level=structuredClone(base), wall=base.walls.find(w=>w.id===id), gone=new Set([id]);
  // A room from the app lists its own copy of a wall it shares, and the app removes the stretch of
  // each copy the wall covers along with it (WallConnectionService.FindOppositeWallsForSplit): the
  // rooms on both sides become one, and a longer copy keeps what lies beyond the wall's ends. A room
  // laid over this one keeps its walls.
  const room=wall && base.rooms.find(r=>r.index===wall.roomIndex);
  if(wall) for(const copy of base.walls) {
    const along=unit(wall);
    if(copy.id===id || copy.roomIndex===wall.roomIndex || Math.abs(cross(sub(copy.start,wall.start),along))>=EPS || Math.abs(cross(sub(copy.end,wall.start),along))>=EPS) continue;
    const s=dot(sub(copy.start,wall.start),along), e=dot(sub(copy.end,wall.start),along);
    if(Math.min(wall.length,Math.max(s,e))-Math.max(0,Math.min(s,e))<=EPS) continue;
    const other=base.rooms.find(r=>r.index===copy.roomIndex);
    if(room && other && roomsOverlap(room.polygon,other.polygon)) continue;
    const host=level.walls.find(w=>w.id===copy.id)!;
    const cuts=[wall.start,wall.end].map(p=>dot(sub(p,host.start),unit(host))).filter(t=>t>EPS && t<host.length-EPS);
    if(cuts.length && splitSegments(level,host,cuts,false,'start',EPS)) continue;
    for(const piece of level.walls) if(piece.roomIndex===copy.roomIndex && [piece.start,piece.end].every(p=>onSegment(p,copy.start,copy.end)&&onSegment(p,wall.start,wall.end))) gone.add(piece.id);
  }
  const involved=new Set(level.walls.filter(w=>gone.has(w.id)).map(w=>w.roomIndex));
  level.walls=level.walls.filter(w=>!gone.has(w.id));
  level.openings=level.openings.filter(o=>!gone.has(o.wallId));
  level.items=level.items.filter(i=>!i.wallId||!gone.has(i.wallId));
  // A wall of those rooms left with a loose end on the removed wall's line is drawn to the removed
  // wall's nearer end, as WallRemoveHandler pulls a corner that "crawled" with a different thickness:
  // a copy a few centimetres shorter than the wall it doubled would otherwise leave a gap, and the
  // joined room would not close. The app pulls from any distance and so turns a wall ending halfway
  // along the removed one askew; here the pull stops at the app's own joint tolerance, Wall.Epsilon.
  const moved: Array<{from:Vec2;to:Vec2}>=[];
  if(wall) for(const w of level.walls) {
    if(!involved.has(w.roomIndex) || Math.abs(cross(unit(w),unit(wall)))<.02) continue;
    for(const end of ['start','end'] as const) {
      const p=w[end];
      if(!onSegment(p,wall.start,wall.end) || level.walls.some(o=>o!==w && (samePoint(o.start,p)||samePoint(o.end,p)))) continue;
      const to=distance(p,wall.start)<=distance(p,wall.end)?wall.start:wall.end, other=end==='start'?w.end:w.start;
      if(samePoint(p,to) || distance(p,to)>WALL_JOINT || distance(to,other)<MIN_WALL) continue;
      const centres=level.openings.filter(o=>o.wallId===w.id).map(o=>[o,plus(w.start,mul(unit(w),o.offset))] as const);
      w[end]={...to};refreshWall(w);
      for(const [o,centre] of centres) o.offset=Math.max(o.width/2,Math.min(w.length-o.width/2,dot(sub(centre,w.start),unit(w))));
      moved.push({from:p,to});
    }
  }
  healSeams(level);
  const previous=base.rooms.map(r=>({...r,polygon:r.polygon.map(p=>moved.find(m=>samePoint(m.from,p))?.to??p)}));
  rebuildRooms(level,previous,base);
  // Halves the rooms on both sides held apart are one room's now: they join, and the room drops the
  // corner their joint made.
  const walls=level.walls.length;
  healSeams(level);
  if(level.walls.length<walls) rebuildRooms(level,level.rooms,base);
  return {level};
}

function availableId(level: Level, seed: string): string {
  let id = seed, suffix = 0;
  while (level.walls.some(w => w.id === id)) id = `${seed}:${++suffix}`;
  return id;
}

const onEdge = (poly: Vec2[], p: Vec2) => poly.some((a, i) => projectPoint(p, a, poly[(i + 1) % poly.length]).distance < EPS);

/**
 * The room's own walls: the contour it runs along, plus the loose walls standing inside it.
 * Falling merely inside the outline is not enough — once rooms may lie over one another, a
 * neighbour's walls cross this room's floor without ever belonging to it, and treating them as
 * this room's would drag them along and leave the originals behind as debris.
 */
export function roomWalls(level: Level, index: number): Wall[] {
  const room = level.rooms.find(r => r.index === index);
  if (!room) return [];
  const others = level.rooms.filter(r => r.index !== index);
  return level.walls.filter(w => {
    const points = [w.start, w.center, w.end];
    // A boundary shared with a neighbour is on both contours and belongs to both.
    if (points.every(p => onEdge(room.polygon, p))) return true;
    if (!points.every(p => contains(room.polygon, p) || onEdge(room.polygon, p))) return false;
    // Inside the outline, but owned by another room or tracing another room's contour.
    if (w.roomIndex !== index && others.some(r => r.index === w.roomIndex)) return false;
    return !others.some(r => points.every(p => onEdge(r.polygon, p)));
  });
}

/**
 * A room sitting wholly inside another one, the way the native editor allows: the contours are
 * nested and never cross. Distinguishes that legal arrangement from a partial overlap.
 */
export function polygonNested(outer: Vec2[], inner: Vec2[]): boolean {
  if (Math.abs(polygonArea(inner)) >= Math.abs(polygonArea(outer)) - EPS) return false;
  // Nested means clear of the outer contour. A room touching the walls of the one around it is
  // overlapping it, not sitting inside it, and must keep its own walls instead of welding in.
  if (!inner.every(p => contains(outer, p) && !onEdge(outer, p)) || !contains(outer, roomProbe(inner))) return false;
  return !outer.some((a, i) => inner.some((c, j) => {
    const hit = intersection({ start: a, end: outer[(i + 1) % outer.length] } as Wall, { start: c, end: inner[(j + 1) % inner.length] } as Wall);
    return !!hit && hit.t > EPS && hit.t < 1 - EPS && hit.u > EPS && hit.u < 1 - EPS;
  }));
}

function roomsOverlap(a: Vec2[], b: Vec2[]): boolean {
  const inside = (polygon: Vec2[], p: Vec2) => contains(polygon, p) && !onEdge(polygon, p);
  if (a.some(p => inside(b, p)) || b.some(p => inside(a, p)) || inside(a, roomProbe(b)) || inside(b, roomProbe(a))) return true;
  // Two rooms can overlap in a strip that holds no vertex of either — slide one sideways onto a
  // neighbour of equal depth and every corner lands on the other's wall line. Sample the cells the
  // two outlines cut the plane into, which separates a real overlap from rooms merely brought
  // up against each other.
  const xs = [...new Set([...a, ...b].map(p => p.x))].sort((m, n) => m - n);
  const zs = [...new Set([...a, ...b].map(p => p.z))].sort((m, n) => m - n);
  for (let i = 1; i < xs.length; i++) for (let j = 1; j < zs.length; j++) {
    const p = { x: (xs[i - 1] + xs[i]) / 2, z: (zs[j - 1] + zs[j]) / 2 };
    if (inside(a, p) && inside(b, p)) return true;
  }
  return false;
}

export function moveRoom(base: Level, index: number, delta: Vec2): EditResult {
  const room = base.rooms.find(r => r.index === index);
  if (!room) return fail(tr('#GeometryRoomNotFound'));
  if (!finite(delta)) return fail(tr('#GeometryEnterFiniteRoomPosition'));
  if (delta.x === 0 && delta.z === 0) return { level: base };
  const translate = (p: Vec2) => snapPrecision(plus(p, delta));
  const polygon = room.polygon.map(translate);
  // Rooms are independent contours, as in the native editor: one laid over another is a legal
  // arrangement, not broken geometry. A room dropped over its neighbour keeps its own walls
  // instead of welding into them, so nothing is split at a crossing the user did not ask for.
  const overlapping = base.rooms.some(r => r.index !== index && roomsOverlap(polygon, r.polygon)
    && !polygonNested(polygon, r.polygon) && !polygonNested(r.polygon, polygon));
  const owned = new Set(roomWalls(base, index).map(w => w.id));
  if (!owned.size) return fail(tr('#GeometryRoomNoClosedContour'));
  const shared = new Set(base.rooms.filter(r => r.index !== index).flatMap(r => roomWalls(base, r.index).map(w => w.id)).filter(id => owned.has(id)));
  const level = { ...base, walls: base.walls.map(w => ({ ...w })), openings: base.openings.map(o => ({ ...o })), items: base.items.slice() };
  const movingIds = new Set<string>(), detachedIds = new Map<string, string>();
  for (const wall of [...level.walls]) {
    if (!owned.has(wall.id)) continue;
    let moving = wall;
    if (shared.has(wall.id)) {
      moving = { ...wall, id: availableId(level, `${wall.id}:room:${index}`) };
      detachedIds.set(wall.id, moving.id); level.walls.push(moving);
      // The half left behind now bounds only the neighbour, so it takes that room's identity.
      const neighbour = base.rooms.find(r => r.index !== index && roomWalls(base, r.index).some(w => w.id === wall.id));
      if (neighbour) wall.roomIndex = neighbour.index;
    }
    // The travelling walls belong to the room being moved. A wall detached from a shared boundary
    // still carries its old owner, and without this it would read as the neighbour's wall and its
    // crossings would be judged as one room's contour cutting itself.
    moving.roomIndex = index;
    movingIds.add(moving.id); moving.start = translate(wall.start); moving.end = translate(wall.end); refreshWall(moving);
  }
  level.items = base.items.map(item => {
    let travels = contains(room.polygon, item.position);
    if (item.wallId) {
      const wall = base.walls.find(w => w.id === item.wallId);
      travels = owned.has(item.wallId) && (!shared.has(item.wallId) || !!wall && contains(room.polygon,
        plus(wall.center, mul(sub(item.position, wall.center), .01 / Math.max(distance(item.position, wall.center), EPS)))));
    }
    return travels ? { ...item, position: translate(item.position), ...(item.wallId ? { wallId: detachedIds.get(item.wallId) ?? item.wallId } : {}) } : item.wallId ? { ...item } : item;
  });
  const previous = base.rooms.map(r => {
    if (r.index !== index) return r;
    const moved = { ...r, polygon };
    for (const key of ['floorSurface', 'ceilingSurface'] as const) {
      const surface = r[key];
      if (!surface?.texture && !surface?.customTexture) continue;
      const t = textureAffine(surface);
      moved[key] = { ...surface, offset: [t.e - t.a * delta.x + t.c * delta.z, t.f - t.b * delta.x + t.d * delta.z] };
    }
    return moved;
  });
  level.rooms = previous;
  // Welding is for a room brought up against its neighbours; a room laid over one keeps its own
  // contour, so the walls it crosses are left whole.
  if (!overlapping) {
    const connectionError = connectEndpoints(level, movingIds, false, true, previous.find(r => r.index === index), oldJunctions(base));
    if (connectionError) return fail(connectionError);
  }
  collapseTwinOpenings(level, base);
  const error = validateLevel(level, base); if (error) return fail(error);
  rebuildRooms(level, previous);
  // Compare the contour itself, not the reported area: gaining a nested room legitimately
  // shrinks the reported area of the room that now encloses it.
  const moved = level.rooms.find(r => r.index === index);
  if (level.rooms.length !== base.rooms.length || !moved
    || Math.abs(Math.abs(polygonArea(moved.polygon)) - Math.abs(polygonArea(room.polygon))) > EPS)
    return fail(tr('#GeometryMoveBreaksRoomOutline'));
  return { level };
}

export type CloneResult = { level: Level; index: number; created: string[]; error?: undefined } | { level?: undefined; error: string };

const refuse = (error: string): CloneResult => ({ error });

/** The box around an outline. */
function bounds(points: readonly Vec2[]): { x0: number; x1: number; z0: number; z1: number } {
  const xs = points.map(p => p.x), zs = points.map(p => p.z);
  return { x0: Math.min(...xs), x1: Math.max(...xs), z0: Math.min(...zs), z1: Math.max(...zs) };
}

/** A room's floor or ceiling finish taken along with it: a picture keeps its place on the floor, as a moved room's does. */
function carriedSurface(surface: Surface | undefined, delta: Vec2): Surface | undefined {
  if (!surface) return undefined;
  if (!surface.texture && !surface.customTexture) return structuredClone(surface);
  const t = textureAffine(surface);
  return { ...structuredClone(surface), offset: [t.e - t.a * delta.x + t.c * delta.z, t.f - t.b * delta.x + t.d * delta.z] };
}

/**
 * Whether an item belongs to a room: it stands on its floor or hangs from its ceiling — inside its outline and not in a
 * room laid inside it —, or it hangs on the room's face of one of its walls.
 */
export function roomHolds(level: Level, room: Room, item: Item): boolean {
  const nested = level.rooms.filter(r => r.index !== room.index && polygonNested(room.polygon, r.polygon));
  const inside = (p: Vec2) => contains(room.polygon, p) && !onEdge(room.polygon, p) && !nested.some(r => contains(r.polygon, p));
  const wall = item.wallId ? level.walls.find(w => w.id === item.wallId) : undefined;
  if (!wall) return inside(item.position);
  // The point just off the face the item hangs on, beside it.
  const side = Math.sign(dot(sub(item.position, wall.center), wall.normalIn)) || 1;
  const foot = plus(wall.center, mul(unit(wall), dot(sub(item.position, wall.center), unit(wall))));
  return inside(plus(foot, mul(wall.normalIn, side * (wall.thickness / 2 + .02))));
}

/**
 * «Удалить комнату» (the app's RemoveRoom → RoomController.Remove): the room goes with the walls only it stands on —
 * the stretches of its contour no other room runs along and the walls loose inside it — and their doors and windows,
 * with what stands on its floor, hangs from its ceiling and hangs on its face of the walls that stay. A wall another
 * room's contour runs along stays that room's wall, doors and windows with it; the room's own copy of it, as a project
 * from the app lists a shared wall once per room, goes. A room laid inside it stays. Refused while the walls left
 * would still close its outline: a room here is found from its walls, and the outline would come back at once as an
 * empty room.
 */
export function removeRoom(base: Level, index: number): EditResult {
  const room = base.rooms.find(r => r.index === index);
  if (!room) return fail(tr('#GeometryRoomNotFound'));
  const others = base.rooms.filter(r => r.index !== index), nested = others.filter(r => polygonNested(room.polygon, r.polygon));
  const alongOther = (w: Wall) => others.some(r => [w.start, w.center, w.end].every(p => onEdge(r.polygon, p)));
  // Another room's own copy lies along the whole of this wall.
  const copied = (w: Wall) => [0, .25, .5, .75, 1].every(t => {
    const p = plus(w.start, mul(sub(w.end, w.start), t));
    return base.walls.some(o => o.id !== w.id && o.roomIndex !== index && Math.abs(cross(unit(o), unit(w))) < EPS && onSegment(p, o.start, o.end));
  });
  const gone = new Set(roomWalls(base, index).filter(w => !alongOther(w) || (w.roomIndex === index && copied(w))).map(w => w.id));
  const level = structuredClone(base);
  level.walls = level.walls.filter(w => !gone.has(w.id));
  level.openings = level.openings.filter(o => !gone.has(o.wallId));
  level.items = level.items.filter(item => !(item.wallId && gone.has(item.wallId)) && !roomHolds(base, room, item));
  healSeams(level);
  rebuildRooms(level, others, base);
  const walls = level.walls.length;
  healSeams(level);
  if (level.walls.length < walls) rebuildRooms(level, level.rooms, base);
  const inside = (p: Vec2) => contains(room.polygon, p) && !onEdge(room.polygon, p) && !nested.some(r => contains(r.polygon, p));
  if (level.rooms.some(r => !others.some(o => o.index === r.index) && inside(roomProbe(r.polygon))))
    return fail(tr('#GeometryRoomEnclosed'));
  const error = validateLevel(level, base); if (error) return fail(error);
  return { level };
}

/**
 * A room of `source` — this floor, or a ready room's project — laid onto `base` moved by `delta`, as a room of its own:
 * its walls with their finishes, plinths, doors and windows, its floor, ceiling, name and type and, `withFurniture`,
 * what it holds, each with a fresh id. With `height` the copy takes that height, its walls with it and what hangs from
 * its ceiling moving up or down by as much. It is laid where nothing touches it: what the room already breaks — an
 * import's walls of one room lying over one another — it may repeat; only what it breaks beyond that refuses it.
 */
function copyRoom(base: Level, source: Level, room: Room, delta: Vec2, withFurniture: boolean, height?: number): CloneResult {
  // The room's own copy of a wall is enough where a neighbour's copy lies along it too.
  const own = roomWalls(source, room.index);
  const walls = own.filter(w => w.roomIndex === room.index || !own.some(o => o.roomIndex === room.index && Math.abs(cross(unit(o), unit(w))) < EPS
    && onSegment(w.start, o.start, o.end) && onSegment(w.end, o.start, o.end)));
  if (!walls.length) return refuse(tr('#GeometryRoomNoClosedContour'));
  const moved = (p: Vec2) => snapPrecision(plus(p, delta));
  const lift = height === undefined ? 0 : height - room.height;
  const level = structuredClone(base), copyIndex = Math.max(-1, ...base.rooms.map(r => r.index)) + 1;
  const wallIds = new Map(walls.map(w => [w.id, `w_${crypto.randomUUID()}`]));
  for (const w of walls) {
    const copy: Wall = { ...structuredClone(w), id: wallIds.get(w.id)!, start: moved(w.start), end: moved(w.end), roomIndex: copyIndex, ...(height === undefined ? {} : { height }) };
    // A wall the room shares faces the room next door, and its plinth and molding are that room's.
    if (w.roomIndex !== room.index) { delete copy.plinth; delete copy.molding; }
    refreshWall(copy); level.walls.push(copy);
  }
  for (const o of source.openings) if (wallIds.has(o.wallId)) level.openings.push({ ...structuredClone(o), id: `o_${crypto.randomUUID()}`, wallId: wallIds.get(o.wallId)! });
  const created: string[] = [];
  if (withFurniture) for (const item of source.items) {
    if (!roomHolds(source, room, item) || (item.wallId && !wallIds.has(item.wallId))) continue;
    const copy: Item = { ...structuredClone(item), id: `i_${crypto.randomUUID()}`, position: moved(item.position), ...(item.wallId ? { wallId: wallIds.get(item.wallId)! } : {}),
      ...(item.placement === 'ceiling' && lift ? { elevation: Math.max(0, item.elevation + lift) } : {}) };
    level.items.push(copy); created.push(copy.id);
  }
  const copy: Room = { ...structuredClone(room), index: copyIndex, polygon: room.polygon.map(moved), ...(height === undefined ? {} : { height }),
    floorSurface: carriedSurface(room.floorSurface, delta), ceilingSurface: carriedSurface(room.ceilingSurface, delta) };
  if (!copy.floorSurface) delete copy.floorSurface;
  if (!copy.ceilingSurface) delete copy.ceilingSurface;
  const repeated = { ...base, walls: level.walls.slice(), openings: level.openings.slice() };
  rebuildRooms(level, [...base.rooms, copy], base);
  const error = validateLevel(level, repeated); if (error) return refuse(error);
  const probe = plus(roomProbe(room.polygon), delta);
  const placed = level.rooms.find(r => contains(r.polygon, probe) && sameOutline(r.polygon, copy.polygon));
  if (!placed) return refuse(tr('#GeometryRoomCopyNotClosed'));
  return { level, index: placed.index, created };
}

/**
 * «Клонировать» a room (the app's CloneRoom → RoomController.Clone): a copy of it with its walls, their finishes,
 * plinths, doors and windows, its floor and ceiling, its name and type; `withFurniture` («Клонировать всю комнату»)
 * brings what stands on its floor, hangs from its ceiling and hangs on its face of its walls, as the app's
 * Clone(withFurniture) gates only the furniture. The copy stands at the free place nearest to the room beside a room
 * of the floor — to its left, right, front or back, touching it, as the app's GetDeltaToMove chooses — brought there
 * as a room dragged against its neighbours is (`moveRoom`). Where the plan will not weld it there, it stands a metre
 * off such a room, and with no place free at all a metre to the right of the floor. The copy's number and the new
 * items' ids come back with the level.
 */
export function cloneRoom(base: Level, index: number, withFurniture: boolean): CloneResult {
  const room = base.rooms.find(r => r.index === index);
  if (!room) return refuse(tr('#GeometryRoomNotFound'));
  const box = bounds(room.polygon), centre = { x: (box.x0 + box.x1) / 2, z: (box.z0 + box.z1) / 2 };
  const plan = bounds([...base.walls.flatMap(w => [w.start, w.end]), ...base.rooms.flatMap(r => r.polygon)]);
  // The copy, first where nothing touches it.
  const far = snapPrecision({ x: plan.x1 + 1 - box.x0, z: 0 });
  const copied = copyRoom(base, base, room, far, withFurniture);
  if (!copied.level) return copied;
  const { level, index: placed, created } = copied;
  // Then brought to the nearest free place beside a room, as the app places it.
  const free = (delta: Vec2) => {
    const polygon = room.polygon.map(p => plus(p, delta));
    if (base.rooms.some(r => roomsOverlap(polygon, r.polygon))) return false;
    return !base.walls.some(w => [w.start, w.center, w.end].some(p => contains(polygon, p) && !onEdge(polygon, p))
      || polygon.some((a, i) => { const hit = intersection({ start: a, end: polygon[(i + 1) % polygon.length] } as Wall, w); return !!hit && hit.t > EPS && hit.t < 1 - EPS && hit.u > EPS && hit.u < 1 - EPS; }));
  };
  const width = box.x1 - box.x0, depth = box.z1 - box.z0;
  const around = (gap: number) => base.rooms.flatMap(r => {
    const b = bounds(r.polygon), x = (b.x0 + b.x1) / 2, z = (b.z0 + b.z1) / 2;
    return [{ x: b.x0 - gap - width / 2, z }, { x: b.x1 + gap + width / 2, z }, { x, z: b.z0 - gap - depth / 2 }, { x, z: b.z1 + gap + depth / 2 }];
  }).map(spot => snapPrecision(sub(spot, centre))).filter(free).sort((a, b) => Math.hypot(a.x, a.z) - Math.hypot(b.x, b.z));
  // Where the plan will not weld it — a doorway or a stub of wall where its walls would meet — a metre off instead.
  for (const spot of [...around(0), ...around(1)]) {
    const result = moveRoom(level, placed, sub(spot, far));
    const there = result.level?.rooms.find(r => contains(r.polygon, plus(roomProbe(room.polygon), spot)));
    if (result.level && there) return { level: result.level, index: there.index, created };
  }
  return { level, index: placed, created };
}

/**
 * A ready room — the room of one of the app's shape projects («Добавить новую комнату», `AddNewRoomList` →
 * `Level.PlaceRoom`) — added to the floor with its walls, doors, windows, finishes and lamp, at the floor's height. On
 * an empty floor it stays where the project has it; beside a plan wider than deep it goes above the plan, 40 cm off
 * (twice the app's wall), its lowest-left corner over the plan's top-left one, and beside any other plan to its right,
 * its left-bottom corner level with the plan's right-bottom one. The room's number and its items' ids come back.
 */
export function addShapedRoom(base: Level, shape: Level): CloneResult {
  const room = shape.rooms[0];
  if (!room) return refuse(tr('#GeometryShapeHasNoRoom'));
  const taken = [...base.rooms.flatMap(r => r.polygon), ...base.walls.flatMap(w => [w.start, w.end])];
  let delta: Vec2 = { x: 0, z: 0 };
  if (taken.length) {
    const plan = bounds(taken), gap = 2 * DEFAULT_WALL_THICKNESS;
    // The app's plan runs y up where this one runs z down: its "above" is the least z here, its "bottom" the greatest.
    const pick = (points: readonly Vec2[], better: (p: Vec2, best: Vec2) => boolean) => points.reduce((best, p) => better(p, best) ? p : best);
    if (plan.x1 - plan.x0 > plan.z1 - plan.z0) {
      const from = pick(room.polygon, (p, best) => p.z > best.z + EPS || Math.abs(p.z - best.z) <= EPS && p.x < best.x);
      const to = pick(taken.filter(p => Math.abs(p.z - plan.z0) <= EPS), (p, best) => p.x < best.x);
      delta = { x: to.x - from.x, z: to.z - from.z - gap };
    } else {
      const from = pick(room.polygon, (p, best) => p.x < best.x - EPS || Math.abs(p.x - best.x) <= EPS && p.z > best.z);
      const to = pick(taken.filter(p => Math.abs(p.x - plan.x1) <= EPS), (p, best) => p.z > best.z);
      delta = { x: to.x - from.x + gap, z: to.z - from.z };
    }
  }
  return copyRoom(base, shape, room, snapPrecision(delta), true, base.height);
}
